import {
  createContext,
  useContext,
  useState,
  useCallback,
  useMemo,
  useRef,
  useEffect,
  type ReactNode
} from 'react'
import { useTranslation } from 'react-i18next'
import { setAudioLocale } from '../../lib/audio'

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

/**
 * Reasons the kiosk session can be reset to the start screen.
 *
 * @since 1.0.0
 */
export type ResetReason =
  | 'timeout'
  | 'interruption'
  | 'offline'
  | 'completion'

/**
 * Shared accessibility/session state for the FrontDesk self-service shell.
 *
 * Coordinates session language, readability mode, reset/reassurance overlay,
 * and timed terminal-state extension handling across all self-service flows.
 *
 * @since 1.0.0
 */
export interface AccessibilitySessionValue {
  // -- Language ----------------------------------------------------------

  /** Admin-configured default language, loaded from settings on mount */
  adminDefaultLanguage: string
  /** Current session language (patient override or admin default) */
  sessionLanguage: string
  /** Change the session language (patient override) */
  setSessionLanguage: (lang: string) => void
  /** Initial selection starts a presentation session, including the service menu. */
  beginPresentationSession: (lang: string) => void
  /** Whether the patient has completed initial entry and owns the presentation. */
  presentationSessionStarted: boolean

  // -- Readability -------------------------------------------------------

  /** Whether readability mode is active for this session */
  readabilityMode: boolean
  /** Toggle readability mode on/off */
  toggleReadabilityMode: () => void

  // -- Reset / Reassurance -----------------------------------------------

  /** Whether the reset/reassurance overlay is currently visible */
  showResetOverlay: boolean
  /** The reason the current reset was triggered, or null */
  resetReason: ResetReason | null
  /**
   * Trigger the shared reset/reassurance overlay.
   * The overlay will display for a short period, then call `onResetComplete`.
   */
  triggerReset: (reason: ResetReason) => void
  /** Dismiss the overlay and return to start (called by overlay or timeout) */
  completeReset: () => void

  // -- Timed terminal extension ------------------------------------------

  /**
   * Extend the active timed terminal countdown.
   * This is a signal from the shell to the active timed screen.
   * The timed screen registers a listener via `onExtendTime`.
   */
  requestExtendTime: () => void
  /** Register a listener for extend-time requests. Returns cleanup fn. */
  onExtendTime: (handler: () => void) => () => void

  // -- Session lifecycle -------------------------------------------------

  /** Reset all session overrides back to admin defaults (for fresh session) */
  resetSession: () => void
}

const AccessibilitySessionContext =
  createContext<AccessibilitySessionValue | null>(null)

// ---------------------------------------------------------------------------
// Provider
// ---------------------------------------------------------------------------

export interface AccessibilitySessionProviderProps {
  children: ReactNode
  /** Admin-configured default language from settings persistence */
  adminDefaultLanguage: string
  /** Called when the reset/reassurance overlay completes and the shell should return to start */
  onResetComplete: () => void
}

/**
 * Provider for the shared accessibility/session state.
 *
 * @since 1.0.0
 * @example
 * ```tsx
 * <AccessibilitySessionProvider
 *   adminDefaultLanguage="de"
 *   onResetComplete={() => setActiveFlow(null)}
 * >
 *   <SelfServiceContent />
 * </AccessibilitySessionProvider>
 * ```
 */
export function AccessibilitySessionProvider({
  children,
  adminDefaultLanguage,
  onResetComplete,
}: AccessibilitySessionProviderProps) {
  const { i18n } = useTranslation()

  // -- Language state -----------------------------------------------------
  const [patientLanguageOverride, setPatientLanguageOverride] = useState<string | null>(null)
  const sessionLanguage = patientLanguageOverride ?? adminDefaultLanguage
  const [presentationSessionStarted, setPresentationSessionStarted] = useState(false)
  const beginPresentationSession = useCallback((lang: string) => {
    setPatientLanguageOverride(lang)
    setPresentationSessionStarted(true)
  }, [])

  const setSessionLanguage = useCallback(
    (lang: string) => {
      setPatientLanguageOverride(lang)
    },
    []
  )

  useEffect(function syncSessionLanguage() {
    setAudioLocale(sessionLanguage)
    void i18n.changeLanguage(sessionLanguage)
  }, [i18n, sessionLanguage])

  // -- Readability --------------------------------------------------------
  const [readabilityMode, setReadabilityMode] = useState(false)

  const toggleReadabilityMode = useCallback(() => {
    setReadabilityMode((prev) => !prev)
  }, [])

  const extendTimeHandlerRef = useRef<(() => void) | null>(null)

  // -- Reset overlay ------------------------------------------------------
  const [showResetOverlay, setShowResetOverlay] = useState(false)
  const [resetReason, setResetReason] = useState<ResetReason | null>(null)

  const triggerReset = useCallback((reason: ResetReason) => {
    setResetReason(reason)
    setShowResetOverlay(true)
  }, [])

  const completeReset = useCallback(() => {
    // Reset all session overrides before returning to start
    setPatientLanguageOverride(null)
    setPresentationSessionStarted(false)
    setReadabilityMode(false)
    setShowResetOverlay(false)
    setResetReason(null)
    extendTimeHandlerRef.current = null
    onResetComplete()
  }, [onResetComplete])

  // -- Timed terminal extension -------------------------------------------

  const requestExtendTime = useCallback(() => {
    extendTimeHandlerRef.current?.()
  }, [])

  const onExtendTime = useCallback((handler: () => void) => {
    extendTimeHandlerRef.current = handler
    return () => {
      extendTimeHandlerRef.current = null
    }
  }, [])

  // -- Session lifecycle --------------------------------------------------
  const resetSession = useCallback(() => {
    setPatientLanguageOverride(null)
    setPresentationSessionStarted(false)
    setReadabilityMode(false)
    setShowResetOverlay(false)
    setResetReason(null)
    extendTimeHandlerRef.current = null
  }, [])

  const sessionValue = useMemo<AccessibilitySessionValue>(
    () => ({
      adminDefaultLanguage,
      sessionLanguage,
      setSessionLanguage,
      beginPresentationSession,
      presentationSessionStarted,
      readabilityMode,
      toggleReadabilityMode,
      showResetOverlay,
      resetReason,
      triggerReset,
      completeReset,
      requestExtendTime,
      onExtendTime,
      resetSession,
    }),
    [
      adminDefaultLanguage,
      beginPresentationSession,
      presentationSessionStarted,
      completeReset,
      onExtendTime,
      readabilityMode,
      requestExtendTime,
      resetReason,
      resetSession,
      sessionLanguage,
      setSessionLanguage,
      showResetOverlay,
      toggleReadabilityMode,
      triggerReset,
    ],
  )

  return (
    <AccessibilitySessionContext.Provider
      value={sessionValue}
    >
      {children}
    </AccessibilitySessionContext.Provider>
  )
}

// ---------------------------------------------------------------------------
// Hook
// ---------------------------------------------------------------------------

/**
 * Access the shared accessibility/session state.
 *
 * @since 1.0.0
 */
export function useAccessibilitySession(): AccessibilitySessionValue {
  const ctx = useContext(AccessibilitySessionContext)
  if (!ctx) {
    throw new Error(
      'useAccessibilitySession must be used within AccessibilitySessionProvider'
    )
  }
  return ctx
}
